import React, { useState, useRef, useEffect } from 'react';
import './DatePicker.css';

interface DatePickerProps {
  value?: string; // mm/dd/yyyy format
  onChange: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value = '',
  onChange,
  placeholder = 'mm/dd/yyyy',
  disabled = false,
  className = '',
  label,
  required = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Parse date from mm/dd/yyyy format
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
  };

  // Format date to mm/dd/yyyy
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Initialize selected date from value
  useEffect(() => {
    if (value) {
      const parsed = parseDate(value);
      if (parsed) {
        setSelectedDate(parsed);
        setCurrentDate(parsed);
        setInputValue(value);
      }
    } else {
      setSelectedDate(null);
      setInputValue('');
    }
  }, [value]);

  // Handle input change with format enforcement
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    
    // Remove non-numeric characters except forward slashes
    input = input.replace(/[^\d/]/g, '');
    
    // Enforce mm/dd/yyyy format
    if (input.length >= 2 && !input.includes('/')) {
      input = input.substring(0, 2) + '/' + input.substring(2);
    }
    if (input.length >= 5 && input.split('/').length === 2) {
      input = input.substring(0, 5) + '/' + input.substring(5);
    }
    
    // Limit to mm/dd/yyyy format
    if (input.length > 10) {
      input = input.substring(0, 10);
    }
    
    setInputValue(input);
    
    // Validate and update if complete
    if (input.length === 10) {
      const parsed = parseDate(input);
      if (parsed) {
        setSelectedDate(parsed);
        onChange(input);
      }
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    const formatted = formatDate(date);
    setInputValue(formatted);
    setSelectedDate(date);
    setCurrentDate(date);
    onChange(formatted);
    setIsOpen(false);
  };

  // Navigate months
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  // Navigate years
  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setFullYear(newDate.getFullYear() - 1);
      } else {
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      
      days.push(
        <button
          key={i}
          className={`calendar-day ${isCurrentMonth ? 'current-month' : 'other-month'} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateSelect(date)}
          disabled={!isCurrentMonth}
        >
          {date.getDate()}
        </button>
      );
    }
    
    return days;
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`date-picker ${className}`} ref={datePickerRef}>
      {label && (
        <label className="date-picker-label">
          {label}
          {required && <span className="required"> *</span>}
        </label>
      )}
      <div className="date-picker-input-container">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className="date-picker-input"
        />
        <button
          type="button"
          className="date-picker-button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
        >
          📅
        </button>
      </div>
      
      {isOpen && (
        <div className="date-picker-calendar">
          <div className="calendar-header">
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigateYear('prev')}
            >
              ««
            </button>
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigateMonth('prev')}
            >
              «
            </button>
            <div className="calendar-month-year">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigateMonth('next')}
            >
              »
            </button>
            <button
              type="button"
              className="calendar-nav-button"
              onClick={() => navigateYear('next')}
            >
              »»
            </button>
          </div>
          
          <div className="calendar-weekdays">
            <div className="weekday">Su</div>
            <div className="weekday">Mo</div>
            <div className="weekday">Tu</div>
            <div className="weekday">We</div>
            <div className="weekday">Th</div>
            <div className="weekday">Fr</div>
            <div className="weekday">Sa</div>
          </div>
          
          <div className="calendar-days">
            {generateCalendarDays()}
          </div>
        </div>
      )}
    </div>
  );
};
